import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ArrowLeftIcon, MailIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
interface Section {
  title: string;
  content: React.ReactNode;
}
const sections: Section[] = [{
  title: '1. Information We Collect',
  content: <div className="space-y-5" data-id="element-3009">
        <p data-id="element-3010">We may collect the following types of information:</p>
        <div data-id="element-3011">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-3012">Personal Information</h4>
          <p className="mb-2" data-id="element-3013">You may voluntarily provide:</p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3014">
            <li data-id="element-3015">Name</li>
            <li data-id="element-3016">Email address</li>
            <li data-id="element-3017">Phone number</li>
            <li data-id="element-3018">Billing/payment information</li>
            <li data-id="element-3019">Mailing address</li>
            <li data-id="element-3020">Account login information</li>
            <li data-id="element-3021">Questionnaire or onboarding responses</li>
            <li data-id="element-3022">Appointment or scheduling details</li>
            <li data-id="element-3023">Travel preferences</li>
            <li data-id="element-3024">Coaching application information</li>
            <li data-id="element-3025">User-generated content, messages, reviews, or feedback</li>
          </ul>
        </div>
        <div data-id="element-3026">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-3027">
            Automatically Collected Information
          </h4>
          <p className="mb-2" data-id="element-3028">
            When you use our website or apps, we may automatically collect:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3029">
            <li data-id="element-3030">Device type</li>
            <li data-id="element-3031">Browser type</li>
            <li data-id="element-3032">IP address</li>
            <li data-id="element-3033">App usage data</li>
            <li data-id="element-3034">Pages visited</li>
            <li data-id="element-3035">Click activity</li>
            <li data-id="element-3036">Referral sources</li>
            <li data-id="element-3037">Cookies and analytics data</li>
            <li data-id="element-3038">Approximate location information</li>
          </ul>
        </div>
        <div data-id="element-3039">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-3040">
            Sensitive or Wellness-Related Information
          </h4>
          <p className="mb-2" data-id="element-3041">
            Some services may involve discussions related to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3042">
            <li data-id="element-3043">Mental wellness</li>
            <li data-id="element-3044">Lifestyle habits</li>
            <li data-id="element-3045">Stress management</li>
            <li data-id="element-3046">Organization and productivity</li>
            <li data-id="element-3047">Personal goals</li>
          </ul>
          <p className="mt-2" data-id="element-3048">
            We do not provide medical or mental health treatment services. Any
            information shared is voluntary.
          </p>
        </div>
      </div>
}, {
  title: '2. How We Use Your Information',
  content: <div data-id="element-3049">
        <p className="mb-3" data-id="element-3050">We may use your information to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3051">
          <li data-id="element-3052">Provide and improve our services</li>
          <li data-id="element-3053">Process purchases and subscriptions</li>
          <li data-id="element-3054">Deliver digital products, courses, or checklists</li>
          <li data-id="element-3055">Schedule coaching sessions or consultations</li>
          <li data-id="element-3056">Respond to inquiries and support requests</li>
          <li data-id="element-3057">Personalize app experiences</li>
          <li data-id="element-3058">Generate customized schedules, reminders, or recommendations</li>
          <li data-id="element-3059">Provide website and app development services</li>
          <li data-id="element-3060">Deliver branding or business support services</li>
          <li data-id="element-3061">Send newsletters, updates, promotions, or announcements</li>
          <li data-id="element-3062">Improve website performance and user experience</li>
          <li data-id="element-3063">Monitor app functionality and prevent abuse</li>
          <li data-id="element-3064">Comply with legal obligations</li>
        </ul>
      </div>
}, {
  title: '3. Coaching & Educational Disclaimer',
  content: <div className="space-y-3" data-id="element-3065">
        <p data-id="element-3066">Trippin' with Mandy may offer:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3067">
          <li data-id="element-3068">Coaching</li>
          <li data-id="element-3069">Motivational content</li>
          <li data-id="element-3070">Wellness discussions</li>
          <li data-id="element-3071">Organization systems</li>
          <li data-id="element-3072">Lifestyle guidance</li>
          <li data-id="element-3073">Travel tips</li>
          <li data-id="element-3074">Educational resources</li>
          <li data-id="element-3075">Business guidance</li>
        </ul>
        <p data-id="element-3076">
          These services are for informational and educational purposes only and
          are not medical, legal, financial, or psychological advice.
        </p>
        <p data-id="element-3077">
          If you are experiencing a medical or mental health emergency, please
          contact a licensed professional or emergency services.
        </p>
      </div>
}, {
  title: '4. Website, App Development & Business Services',
  content: <div className="space-y-3" data-id="element-3078">
        <p data-id="element-3079">Trippin' with Mandy may provide:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3080">
          <li data-id="element-3081">Website design and development</li>
          <li data-id="element-3082">App development assistance</li>
          <li data-id="element-3083">Branding and content creation</li>
          <li data-id="element-3084">AI-assisted business tools</li>
          <li data-id="element-3085">Digital setup assistance</li>
          <li data-id="element-3086">Business consulting and organizational support</li>
        </ul>
        <p data-id="element-3087">
          Clients may voluntarily provide business-related information, branding
          materials, login credentials, project requirements, content, or other
          materials necessary to complete requested services.
        </p>
        <p data-id="element-3088">
          We take reasonable precautions to protect shared information; however,
          clients are responsible for maintaining the security of their own
          accounts, passwords, hosting platforms, third-party services, and
          connected tools.
        </p>
        <p data-id="element-3089">Trippin' with Mandy is not responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3090">
          <li data-id="element-3091">Third-party platform outages</li>
          <li data-id="element-3092">Hosting failures</li>
          <li data-id="element-3093">App store approval decisions</li>
          <li data-id="element-3094">Third-party software limitations</li>
          <li data-id="element-3095">Data loss caused by external platforms or user actions</li>
          <li data-id="element-3096">Ongoing maintenance unless otherwise agreed upon</li>
        </ul>
        <p data-id="element-3097">
          Clients retain ownership of their original content and branding
          materials unless otherwise specified in writing.
        </p>
      </div>
}, {
  title: '5. Travel Services & Recommendations',
  content: <div className="space-y-3" data-id="element-3098">
        <p data-id="element-3099">
          Travel-related information, recommendations, packing guides,
          checklists, and destination suggestions are provided for general
          informational purposes only.
        </p>
        <p data-id="element-3100">Users are responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3101">
          <li data-id="element-3102">Verifying travel requirements</li>
          <li data-id="element-3103">Reviewing airline/cruise policies</li>
          <li data-id="element-3104">Obtaining travel insurance if desired</li>
          <li data-id="element-3105">Confirming passport, visa, and vaccination requirements</li>
        </ul>
        <p data-id="element-3106">
          Trippin' with Mandy is not responsible for third-party travel
          disruptions, cancellations, delays, injuries, or losses.
        </p>
      </div>
}, {
  title: '6. Purchases & Payments',
  content: <div className="space-y-3" data-id="element-3107">
        <p data-id="element-3108">Payments may be processed through third-party providers such as:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3109">
          <li data-id="element-3110">Stripe</li>
          <li data-id="element-3111">PayPal</li>
          <li data-id="element-3112">Gumroad</li>
          <li data-id="element-3113">Google Play</li>
          <li data-id="element-3114">Apple App Store</li>
          <li data-id="element-3115">Other payment processors</li>
        </ul>
        <p data-id="element-3116">We do not store full payment card information on our servers.</p>
        <p data-id="element-3117">
          Please review the privacy policies of any third-party payment provider
          you use.
        </p>
      </div>
}, {
  title: '7. Cookies & Analytics',
  content: <div className="space-y-3" data-id="element-3118">
        <p data-id="element-3119">We may use:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3120">
          <li data-id="element-3121">Cookies</li>
          <li data-id="element-3122">Pixels</li>
          <li data-id="element-3123">Analytics tools</li>
          <li data-id="element-3124">Advertising technologies</li>
        </ul>
        <p data-id="element-3125">These tools help us:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3126">
          <li data-id="element-3127">Understand website traffic</li>
          <li data-id="element-3128">Improve performance</li>
          <li data-id="element-3129">Personalize experiences</li>
          <li data-id="element-3130">Measure marketing effectiveness</li>
        </ul>
        <p data-id="element-3131">You may disable cookies through your browser settings.</p>
      </div>
}, {
  title: '8. Email & Marketing Communications',
  content: <div className="space-y-3" data-id="element-3132">
        <p data-id="element-3133">
          If you subscribe to newsletters, download resources, create an
          account, make a purchase, or contact us through forms, we may send:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3134">
          <li data-id="element-3135">Service updates</li>
          <li data-id="element-3136">Promotional emails</li>
          <li data-id="element-3137">Product announcements</li>
          <li data-id="element-3138">App updates</li>
          <li data-id="element-3139">Event or livestream information</li>
          <li data-id="element-3140">Business or coaching communications</li>
        </ul>
        <p data-id="element-3141">
          You may unsubscribe at any time using the unsubscribe link in emails.
        </p>
      </div>
}, {
  title: '9. Third-Party Services',
  content: <div className="space-y-3" data-id="element-3142">
        <p data-id="element-3143">
          Our services may integrate with third-party platforms, including but
          not limited to:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3144">
          <li data-id="element-3145">Google services</li>
          <li data-id="element-3146">Firebase</li>
          <li data-id="element-3147">TikTok</li>
          <li data-id="element-3148">Calendars</li>
          <li data-id="element-3149">Payment processors</li>
          <li data-id="element-3150">Scheduling systems</li>
          <li data-id="element-3151">Social media platforms</li>
          <li data-id="element-3152">Analytics providers</li>
          <li data-id="element-3153">Hosting providers</li>
          <li data-id="element-3154">AI tools and automation platforms</li>
        </ul>
        <p data-id="element-3155">
          We are not responsible for the privacy practices of third-party
          services.
        </p>
      </div>
}, {
  title: '10. Data Security',
  content: <div className="space-y-3" data-id="element-3156">
        <p data-id="element-3157">We take reasonable measures to protect your information using:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3158">
          <li data-id="element-3159">Secure platforms</li>
          <li data-id="element-3160">Password protections</li>
          <li data-id="element-3161">Encrypted services where available</li>
          <li data-id="element-3162">Limited access controls</li>
        </ul>
        <p data-id="element-3163">
          However, no method of internet transmission or storage is 100% secure.
        </p>
      </div>
}, {
  title: "11. Children's Privacy",
  content: <div className="space-y-3" data-id="element-3164">
        <p data-id="element-3165">
          Our services are not directed toward children under 13 years old. We
          do not knowingly collect personal information from children.
        </p>
        <p data-id="element-3166">
          If we become aware that information from a child under 13 has been
          collected, we will delete it promptly.
        </p>
      </div>
}, {
  title: '12. Your Rights',
  content: <div className="space-y-3" data-id="element-3167">
        <p data-id="element-3168">Depending on your location, you may have rights to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3169">
          <li data-id="element-3170">Access your data</li>
          <li data-id="element-3171">Correct inaccurate information</li>
          <li data-id="element-3172">Request deletion of your data</li>
          <li data-id="element-3173">Opt out of marketing communications</li>
          <li data-id="element-3174">Request information about data usage</li>
        </ul>
        <p data-id="element-3175">To make a request, contact us using the information below.</p>
      </div>
}, {
  title: '13. User Content & Community Features',
  content: <div className="space-y-3" data-id="element-3176">
        <p data-id="element-3177">
          If you post content in public areas, communities, chats, livestreams,
          games, or interactive features, that information may be visible to
          other users.
        </p>
        <p data-id="element-3178">Please avoid sharing sensitive personal information publicly.</p>
      </div>
}, {
  title: '14. App Features & Notifications',
  content: <div className="space-y-3" data-id="element-3179">
        <p data-id="element-3180">Some apps or services may request permission for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3181">
          <li data-id="element-3182">Push notifications</li>
          <li data-id="element-3183">Calendar syncing</li>
          <li data-id="element-3184">Location access</li>
          <li data-id="element-3185">Camera or photo uploads</li>
          <li data-id="element-3186">Contacts or reminders</li>
        </ul>
        <p data-id="element-3187">Permissions can typically be managed in your device settings.</p>
      </div>
}, {
  title: '15. Changes to This Policy',
  content: <div className="space-y-3" data-id="element-3188">
        <p data-id="element-3189">
          We may update this Privacy Policy periodically. Updated versions will
          be posted with a revised effective date.
        </p>
        <p data-id="element-3190">
          Continued use of our services after changes means you accept the
          updated policy.
        </p>
      </div>
}, {
  title: '16. Contact Information',
  content: <div className="space-y-3" data-id="element-3191">
        <p data-id="element-3192">For questions, concerns, or privacy requests, contact:</p>
        <a href="mailto:trippinwithmandy@gmail.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blush-100 hover:bg-blush-200 text-plum-800 font-bold transition-colors" data-id="element-3193">
          <MailIcon className="w-5 h-5" data-id="element-3194" />
          trippinwithmandy@gmail.com
        </a>
      </div>
}];
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-lavender-50 pt-28 pb-24" data-id="element-3195">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3196">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-plum-500 hover:text-plum-700 font-bold text-sm mb-8 transition-colors" data-id="element-3197">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3198" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-3199">
          <div className="flex justify-center mb-4" data-id="element-3200">
            <GlitterHeart size={32} data-id="element-3201" />
          </div>
          <div className="w-16 h-16 rounded-2xl bg-blush-100 flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-3202">
            <ShieldCheckIcon className="w-8 h-8 text-cta-500" data-id="element-3203" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-3" data-id="element-3204">
            Privacy Policy
          </h1>
          <p className="text-plum-500 font-medium" data-id="element-3205">For Trippin' with Mandy</p>
          <p className="text-plum-400 text-sm font-medium mt-1" data-id="element-3206">
            Effective Date: May 12, 2026
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100 mb-8" data-id="element-3207">
          <p className="text-plum-700 leading-relaxed mb-4" data-id="element-3208">
            Welcome to Trippin' with Mandy. Your privacy matters to us. This
            Privacy Policy explains how we collect, use, store, and protect your
            information across our websites, apps, digital products, coaching
            services, travel-related services, business services, games,
            communities, and related platforms operated under the Trippin' with
            Mandy brand, including but not limited to Trippin' Through Life
            (TTL) and affiliated projects.
          </p>
          <p className="text-plum-700 leading-relaxed font-medium" data-id="element-3209">
            By using our services, you agree to the practices described in this
            Privacy Policy.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6" data-id="element-3210">
          {sections.map((section, idx) => <motion.section key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.03
        }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100" data-id="element-3211">
              <h2 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-3212">
                {section.title}
              </h2>
              <div className="text-plum-600 leading-relaxed space-y-3" data-id="element-3213">
                {section.content}
              </div>
            </motion.section>)}
        </div>

        {/* Closing */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mt-12 text-plum-500 text-sm font-medium italic px-4" data-id="element-3214">
          By using Trippin' with Mandy services, websites, apps, products,
          games, or digital platforms, you acknowledge that you have read and
          agreed to this Privacy Policy.
        </motion.div>

        {/* Footer links */}
        <div className="text-center mt-10 flex flex-wrap justify-center gap-3" data-id="element-3215">
          <Link to="/terms" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-plum-200 text-plum-700 hover:bg-plum-50 font-bold text-sm transition-colors" data-id="element-3216">
            View Terms & Conditions
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cta-500 text-white hover:bg-cta-600 font-bold text-sm transition-colors shadow-lg shadow-cta-500/20" data-id="element-3217">
            Back to Home
          </Link>
        </div>
      </div>
    </div>;
}